import { useState, useEffect, useCallback } from "react";
import {
  ChevronLeft, ChevronRight, Plus, Trash2, Check, X,
  ChevronDown, ChevronUp, Clock, User, AlertCircle,
  BookOpen, Target, History, Utensils, Calculator, Info, Pencil, Bookmark,
} from "lucide-react";
import VoedingIntakeModal from "../account/VoedingIntakeModal";

const API = process.env.REACT_APP_BACKEND_URL;

function toStr(d) { return d.toISOString().split("T")[0]; }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function fmtDatum(str) {
  if (!str) return "";
  const d = new Date(str + "T12:00:00");
  return d.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" });
}
function fmtTs(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) + " " +
    d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
}

const ACTIE_LABELS = {
  maaltijd_toegevoegd: "Maaltijd toegevoegd",
  maaltijd_verwijderd: "Maaltijd verwijderd",
  doel_gewijzigd:      "Macro-doelen gewijzigd",
  schema_toegewezen:   "Schema toegewezen",
  intake_bijgewerkt:   "Voedingsintake bijgewerkt",
};

// ── Macro balk ─────────────────────────────────────────────────────────────────
function MacroBalk({ waarde, doel, kleur }) {
  const pct = doel > 0 ? Math.min((waarde / doel) * 100, 100) : 0;
  return (
    <div className="w-full h-1.5 bg-white/8 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: kleur }} />
    </div>
  );
}

// ── Maaltijd toevoegen formulier ────────────────────────────────────────────────
function MaaltijdForm({ onSave, onCancel }) {
  const [form, setForm] = useState({ naam: "", calorieen: "", eiwitten: "", koolhydraten: "", vetten: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = "w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40 placeholder-zinc-600";

  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-4 space-y-3">
      <input value={form.naam} onChange={e => set("naam", e.target.value)}
        placeholder="Naam (bijv. Kipfilet 150g)" data-testid="maaltijd-naam-input" className={inp} />
      <div className="grid grid-cols-2 gap-2">
        <input type="number" min={0} value={form.calorieen} onChange={e => set("calorieen", e.target.value)}
          placeholder="Calorieën (kcal)" data-testid="maaltijd-kcal-input" className={inp} />
        <input type="number" min={0} value={form.eiwitten} onChange={e => set("eiwitten", e.target.value)}
          placeholder="Eiwitten (g)" data-testid="maaltijd-eiwit-input" className={inp} />
        <input type="number" min={0} value={form.koolhydraten} onChange={e => set("koolhydraten", e.target.value)}
          placeholder="Koolhydraten (g)" className={inp} />
        <input type="number" min={0} value={form.vetten} onChange={e => set("vetten", e.target.value)}
          placeholder="Vetten (g)" className={inp} />
      </div>
      <div className="flex gap-2">
        <button onClick={onCancel}
          className="flex-1 py-2 rounded-xl border border-white/10 text-zinc-400 text-sm hover:text-white transition-colors">
          Annuleer
        </button>
        <button onClick={() => onSave(form)} disabled={!form.naam.trim() || !form.calorieen}
          data-testid="maaltijd-opslaan-btn"
          className="flex-[2] py-2 rounded-xl text-black text-sm font-bold disabled:opacity-40 transition-all"
          style={{ background: "#C9A84C" }}>
          Toevoegen
        </button>
      </div>
    </div>
  );
}

// ── HOOFD COMPONENT ────────────────────────────────────────────────────────────
export default function SporterVoedingTab({ token, sporter }) {
  const sporterId = sporter.id;

  const [datum,        setDatum]        = useState(toStr(new Date()));
  const [weekData,     setWeekData]     = useState([]);
  const [dagLog,       setDagLog]       = useState(null);
  const [doelen,       setDoelen]       = useState({ calorieen: 2000, eiwitten: 150, koolhydraten: 200, vetten: 65 });
  const [editDoelen,   setEditDoelen]   = useState(false);
  const [doelenForm,   setDoelenForm]   = useState({});
  const [schema,       setSchema]       = useState(null);
  const [schemas,      setSchemas]      = useState([]);
  const [auditLogs,    setAuditLogs]    = useState([]);
  const [auditOpen,    setAuditOpen]    = useState(false);
  const [toonForm,     setToonForm]     = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [toast,        setToast]        = useState(null);
  const [schemaModal,  setSchemaModal]  = useState(false);
  const [selectedSch,  setSelectedSch]  = useState("");
  const [loading,      setLoading]      = useState(true);
  const [intake,       setIntake]       = useState(null);
  const [berekening,   setBerekening]   = useState(null);
  const [showIntake,   setShowIntake]   = useState(false);
  const [eigenMaaltijden, setEigenMaaltijden] = useState([]);
  const [eigenOpen,    setEigenOpen]    = useState(false);
  const [verwijderendId, setVerwijderendId] = useState(null);

  const showToast = (msg, t = "success") => { setToast({ msg, t }); setTimeout(() => setToast(null), 3000); };

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [weekRes, dagRes, schRes, schemaRes, auditRes, intakeRes, eigenRes] = await Promise.all([
        fetch(`${API}/api/nutrition/sporters/${sporterId}/weekoverzicht`, auth),
        fetch(`${API}/api/nutrition/sporters/${sporterId}/log?datum=${datum}`, auth),
        fetch(`${API}/api/nutrition/schemas`, auth),
        fetch(`${API}/api/nutrition/sporters/${sporterId}/schema`, auth),
        fetch(`${API}/api/nutrition/sporters/${sporterId}/audit`, auth),
        fetch(`${API}/api/nutrition/sporters/${sporterId}/intake`, auth),
        fetch(`${API}/api/nutrition/sporters/${sporterId}/eigen-maaltijden`, auth),
      ]);
      if (weekRes.ok) {
        const w = await weekRes.json();
        setWeekData(w.dagen || []);
        const d = w.doelen || {};
        setDoelen({ calorieen: d.calorieen || 2000, eiwitten: d.eiwitten || 150, koolhydraten: d.koolhydraten || 200, vetten: d.vetten || 65 });
        setDoelenForm({ calorieen: d.calorieen || 2000, eiwitten: d.eiwitten || 150, koolhydraten: d.koolhydraten || 200, vetten: d.vetten || 65 });
      }
      if (dagRes.ok) setDagLog(await dagRes.json());
      if (schRes.ok) { const s = await schRes.json(); setSchemas(s.schemas || []); }
      if (schemaRes.ok) { const s = await schemaRes.json(); setSchema(s.schema); }
      if (auditRes.ok) { const a = await auditRes.json(); setAuditLogs(a.logs || []); }
      if (intakeRes.ok) {
        const id = await intakeRes.json();
        setIntake(id.intake || null);
        setBerekening(id.berekening || null);
      }
      if (eigenRes.ok) {
        const ed = await eigenRes.json();
        setEigenMaaltijden(ed.maaltijden || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [sporterId, datum, token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadDag = useCallback(async () => {
    const r = await fetch(`${API}/api/nutrition/sporters/${sporterId}/log?datum=${datum}`, auth);
    if (r.ok) setDagLog(await r.json());
  }, [sporterId, datum, token]);

  const handleSaveDoelen = async () => {
    setSaving(true);
    const r = await fetch(`${API}/api/nutrition/sporters/${sporterId}/doelen`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(doelenForm),
    });
    setSaving(false);
    if (r.ok) {
      setDoelen({ ...doelenForm });
      setEditDoelen(false);
      showToast("Doelen opgeslagen");
    } else { showToast("Opslaan mislukt", "error"); }
  };

  const handleAddMaaltijd = async (form) => {
    setSaving(true);
    const r = await fetch(`${API}/api/nutrition/sporters/${sporterId}/log/maaltijd?datum=${datum}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        naam: form.naam.trim(),
        calorieen: parseFloat(form.calorieen) || 0,
        eiwitten: parseFloat(form.eiwitten) || 0,
        koolhydraten: parseFloat(form.koolhydraten) || 0,
        vetten: parseFloat(form.vetten) || 0,
      }),
    });
    setSaving(false);
    if (r.ok) {
      setToonForm(false);
      await loadDag();
      showToast("Maaltijd toegevoegd");
    } else { showToast("Toevoegen mislukt", "error"); }
  };

  const handleDeleteMaaltijd = async (maaltijdId) => {
    const r = await fetch(`${API}/api/nutrition/sporters/${sporterId}/log/maaltijd/${maaltijdId}?datum=${datum}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) { await loadDag(); showToast("Maaltijd verwijderd"); }
  };

  const handleToewijzenSchema = async () => {
    if (!selectedSch) return;
    setSaving(true);
    const r = await fetch(`${API}/api/nutrition/sporters/${sporterId}/schema/toewijzen`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ schema_id: selectedSch }),
    });
    setSaving(false);
    if (r.ok) { setSchemaModal(false); await loadAll(); showToast("Schema toegewezen"); }
    else { showToast("Toewijzen mislukt", "error"); }
  };

  const handleVerwijderEigenMaaltijd = async (id) => {
    setVerwijderendId(id);
    try {
      await fetch(`${API}/api/nutrition/sporters/${sporterId}/eigen-maaltijden/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setEigenMaaltijden(prev => prev.filter(m => m.id !== id));
      showToast("Opgeslagen maaltijd verwijderd");
    } catch (e) { showToast("Verwijderen mislukt", "error"); }
    setVerwijderendId(null);
  };

  const handleVerwijderSchema = async () => {
    const r = await fetch(`${API}/api/nutrition/sporters/${sporterId}/schema`, {
      method: "DELETE", headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) { setSchema(null); showToast("Schema verwijderd"); }
  };

  const maaltijden = dagLog?.maaltijden || [];
  const totalen = {
    calorieen:   maaltijden.reduce((s, m) => s + (m.calorieen || 0), 0),
    eiwitten:    maaltijden.reduce((s, m) => s + (m.eiwitten || 0), 0),
    koolhydraten: maaltijden.reduce((s, m) => s + (m.koolhydraten || 0), 0),
    vetten:      maaltijden.reduce((s, m) => s + (m.vetten || 0), 0),
  };

  const maxKcal = Math.max(...weekData.map(d => d.totalen?.calorieen || 0), doelen.calorieen, 1);

  if (loading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* ── Voedingsprofiel / Intake ──────────────────────────────────── */}
      <div className={`rounded-xl border p-4 ${intake ? "bg-[#C9A84C]/6 border-[#C9A84C]/20" : "bg-[#0f0f0f] border-white/6 border-dashed"}`}>
        {intake && berekening ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calculator size={13} className="text-[#C9A84C]" />
                <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Voedingsprofiel</p>
              </div>
              <button onClick={() => setShowIntake(true)} data-testid="coach-bewerk-intake-btn"
                className="flex items-center gap-1.5 text-[10px] font-bold text-zinc-400 hover:text-[#C9A84C] transition-colors">
                <Pencil size={11} /> Aanpassen
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[
                { l: "Calorieën", v: `${berekening.calorie_doel} kcal`, c: "#C9A84C" },
                { l: "Eiwitten",  v: `${berekening.eiwitten_doel}g`,    c: "#60a5fa" },
                { l: "Koolhyd.", v: `${berekening.koolhydraten_doel}g`, c: "#34d399" },
                { l: "Vetten",   v: `${berekening.vetten_doel}g`,      c: "#f97316" },
              ].map(m => (
                <div key={m.l} className="bg-[#0f0f0f] rounded-xl p-2.5 text-center">
                  <p className="text-zinc-500 text-[9px] mb-0.5">{m.l}</p>
                  <p className="font-black text-sm tabular-nums" style={{ color: m.c }}>{m.v}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs text-zinc-500">
              <span>Doel: <span className="text-white font-semibold capitalize">{intake.doel?.replace('_',' ')}</span></span>
              <span>Gewicht: <span className="text-white font-semibold">{intake.gewicht} kg</span></span>
              <span>Sport: <span className="text-white font-semibold">{intake.sportdagen_per_week}×/week</span></span>
            </div>
            {intake.ingevuld_door_naam && (
              <p className="text-zinc-600 text-[10px]">
                Ingevuld door: {intake.ingevuld_door_naam} ({intake.ingevuld_door_rol}) · {fmtTs(intake.updated_at)}
              </p>
            )}
            <details className="group">
              <summary className="cursor-pointer text-zinc-500 text-xs flex items-center gap-1 select-none hover:text-zinc-300 transition-colors">
                <Info size={11} /> Berekeningsuitleg
              </summary>
              <p className="text-zinc-500 text-xs mt-2 leading-relaxed bg-[#0f0f0f] rounded-lg p-3">
                {berekening.uitleg}
              </p>
            </details>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm font-semibold mb-0.5">Voedingsprofiel invullen</p>
              <p className="text-zinc-600 text-xs">Geen intake ingevuld voor {sporter.naam || sporter.name}</p>
            </div>
            <button onClick={() => setShowIntake(true)} data-testid="coach-start-intake-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-black text-xs font-bold shrink-0"
              style={{ background: "#C9A84C" }}>
              <Calculator size={11} /> Invullen
            </button>
          </div>
        )}
      </div>

      {/* ── Week overzicht ─────────────────────────────────────────────────── */}
      <div className="bg-[#0f0f0f] border border-white/6 rounded-xl p-4">
        <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-3">Afgelopen 7 dagen</p>
        <div className="flex items-end gap-1.5 h-14">
          {weekData.map(dag => {
            const kcal = dag.totalen?.calorieen || 0;
            const pct  = Math.min((kcal / maxKcal) * 100, 100);
            const isVandaag = dag.datum === toStr(new Date());
            const kleur = !dag.heeft_log ? "#333"
              : kcal >= doelen.calorieen * 0.8 ? "#10B981"
              : kcal >= doelen.calorieen * 0.5 ? "#F59E0B" : "#EF4444";
            return (
              <button key={dag.datum} onClick={() => setDatum(dag.datum)}
                data-testid={`week-dag-${dag.datum}`}
                className="flex-1 flex flex-col items-center gap-1 group">
                <div className="w-full rounded-t-md transition-all group-hover:brightness-125"
                  style={{ height: `${Math.max(pct * 0.88, 4)}%`, background: kleur,
                    outline: datum === dag.datum ? `2px solid #C9A84C` : "none" }} />
                <span className={`text-[9px] font-bold ${isVandaag ? "text-[#C9A84C]" : "text-zinc-600"}`}>
                  {new Date(dag.datum + "T12:00:00").toLocaleDateString("nl-NL", { weekday: "narrow" })}
                </span>
              </button>
            );
          })}
        </div>
        <p className="text-zinc-600 text-[10px] mt-2">Doel: {doelen.calorieen} kcal/dag</p>
      </div>

      {/* ── Datum navigatie + dag-log ──────────────────────────────────────── */}
      <div className="bg-[#0f0f0f] border border-white/6 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setDatum(toStr(addDays(new Date(datum + "T12:00:00"), -1)))}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-zinc-400">
            <ChevronLeft size={14} />
          </button>
          <div className="text-center">
            <p className="text-white font-bold text-sm">{fmtDatum(datum)}</p>
            {datum === toStr(new Date()) && <p className="text-[#C9A84C] text-[10px] font-bold">Vandaag</p>}
          </div>
          <button onClick={() => setDatum(toStr(addDays(new Date(datum + "T12:00:00"), 1)))}
            disabled={datum >= toStr(new Date())}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-zinc-400 disabled:opacity-30">
            <ChevronRight size={14} />
          </button>
        </div>

        {/* Totalen balk */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          {[
            { label: "Kcal", val: Math.round(totalen.calorieen), doel: doelen.calorieen, kleur: "#C9A84C" },
            { label: "Eiwit", val: Math.round(totalen.eiwitten), doel: doelen.eiwitten, kleur: "#3B82F6" },
            { label: "Koolh.", val: Math.round(totalen.koolhydraten), doel: doelen.koolhydraten, kleur: "#10B981" },
            { label: "Vet", val: Math.round(totalen.vetten), doel: doelen.vetten, kleur: "#F59E0B" },
          ].map(m => (
            <div key={m.label} className="bg-[#1a1a1a] rounded-xl p-2.5 text-center">
              <p className="text-white text-sm font-black">{m.val}</p>
              <p className="text-zinc-600 text-[9px] mt-0.5">{m.label}</p>
              <MacroBalk waarde={m.val} doel={m.doel} kleur={m.kleur} />
            </div>
          ))}
        </div>

        {/* Maaltijden lijst */}
        {maaltijden.length === 0 ? (
          <div className="py-4 text-center">
            <Utensils size={18} className="text-zinc-700 mx-auto mb-1.5" />
            <p className="text-zinc-500 text-xs">Geen maaltijden gelogd op deze dag</p>
          </div>
        ) : (
          <div className="space-y-1.5 mb-3">
            {maaltijden.map(m => (
              <div key={m.id} data-testid={`maaltijd-row-${m.id}`}
                className="flex items-center gap-3 px-3 py-2.5 bg-[#1a1a1a] rounded-xl group">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{m.naam}</p>
                  <p className="text-zinc-600 text-[10px]">
                    {Math.round(m.calorieen)} kcal · {Math.round(m.eiwitten)}g E · {Math.round(m.koolhydraten)}g K · {Math.round(m.vetten)}g V
                    {m.toegevoegd_door && (
                      <span className="text-[#C9A84C]/70"> · door {m.toegevoegd_door}</span>
                    )}
                  </p>
                </div>
                <button onClick={() => handleDeleteMaaltijd(m.id)} data-testid={`verwijder-maaltijd-${m.id}`}
                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/0 hover:bg-red-500/15 text-zinc-600 hover:text-red-400 transition-all opacity-0 group-hover:opacity-100">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Maaltijd toevoegen */}
        {toonForm ? (
          <MaaltijdForm
            onSave={handleAddMaaltijd}
            onCancel={() => setToonForm(false)}
          />
        ) : (
          <button onClick={() => setToonForm(true)} data-testid="voeg-maaltijd-toe-btn"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-white/15 text-zinc-500 hover:text-white hover:border-[#C9A84C]/40 text-sm font-semibold transition-all">
            <Plus size={13} /> Maaltijd toevoegen namens sporter
          </button>
        )}
      </div>

      {/* ── Macro doelen ──────────────────────────────────────────────────── */}
      <div className="bg-[#0f0f0f] border border-white/6 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target size={13} className="text-[#C9A84C]" />
            <p className="text-white text-sm font-bold">Macro-doelen</p>
          </div>
          {!editDoelen ? (
            <button onClick={() => setEditDoelen(true)} data-testid="bewerk-doelen-btn"
              className="text-xs text-[#C9A84C] font-bold px-3 py-1.5 rounded-lg hover:bg-[#C9A84C]/10 transition-colors">
              Bewerken
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => setEditDoelen(false)}
                className="text-xs text-zinc-400 font-bold px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
                Annuleer
              </button>
              <button onClick={handleSaveDoelen} disabled={saving} data-testid="sla-doelen-op-btn"
                className="text-xs font-bold px-3 py-1.5 rounded-lg transition-colors text-black disabled:opacity-40"
                style={{ background: "#C9A84C" }}>
                {saving ? "..." : "Opslaan"}
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { key: "calorieen",     label: "Calorieën (kcal)", kleur: "#C9A84C" },
            { key: "eiwitten",      label: "Eiwitten (g)",     kleur: "#3B82F6" },
            { key: "koolhydraten",  label: "Koolhydraten (g)", kleur: "#10B981" },
            { key: "vetten",        label: "Vetten (g)",       kleur: "#F59E0B" },
          ].map(({ key, label, kleur }) => (
            <div key={key} className="bg-[#1a1a1a] rounded-xl p-3">
              <p className="text-[10px] font-semibold mb-1.5" style={{ color: kleur }}>{label}</p>
              {editDoelen ? (
                <input type="number" min={0} value={doelenForm[key]}
                  onChange={e => setDoelenForm(f => ({ ...f, [key]: +e.target.value }))}
                  data-testid={`doel-${key}-input`}
                  className="w-full bg-[#111] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm font-bold focus:outline-none focus:border-[#C9A84C]/40" />
              ) : (
                <p className="text-white text-lg font-black">{doelen[key]}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Voedingsschema ─────────────────────────────────────────────────── */}
      <div className="bg-[#0f0f0f] border border-white/6 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BookOpen size={13} className="text-[#C9A84C]" />
            <p className="text-white text-sm font-bold">Voedingsschema</p>
          </div>
          {schema ? (
            <div className="flex gap-1">
              <button onClick={() => setSchemaModal(true)} data-testid="wijzig-schema-btn"
                className="text-xs text-zinc-400 font-bold px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
                Wijzigen
              </button>
              <button onClick={handleVerwijderSchema}
                className="text-xs text-red-400 font-bold px-3 py-1.5 rounded-lg hover:bg-red-500/10 transition-colors">
                Verwijder
              </button>
            </div>
          ) : (
            <button onClick={() => setSchemaModal(true)} data-testid="toewijzen-schema-btn"
              className="text-xs font-bold px-3 py-1.5 rounded-lg text-black transition-colors"
              style={{ background: "#C9A84C" }}>
              Schema toewijzen
            </button>
          )}
        </div>
        {schema ? (
          <div className="bg-[#1a1a1a] rounded-xl p-3">
            <p className="text-white font-bold text-sm">{schema.naam}</p>
            {schema.beschrijving && <p className="text-zinc-500 text-xs mt-0.5">{schema.beschrijving}</p>}
            <div className="flex gap-4 mt-2">
              {[
                { label: "Kcal", val: schema.calorieen_doel, kleur: "#C9A84C" },
                { label: "Eiwit", val: `${schema.eiwitten_doel}g`, kleur: "#3B82F6" },
                { label: "Koolh.", val: `${schema.koolhydraten_doel}g`, kleur: "#10B981" },
                { label: "Vet", val: `${schema.vetten_doel}g`, kleur: "#F59E0B" },
              ].map(m => (
                <div key={m.label}>
                  <p className="text-[9px] font-semibold mb-0.5" style={{ color: m.kleur }}>{m.label}</p>
                  <p className="text-white text-xs font-bold">{m.val}</p>
                </div>
              ))}
            </div>
            {schema.notities && (
              <p className="text-zinc-500 text-[10px] mt-2 border-t border-white/5 pt-2">{schema.notities}</p>
            )}
            <p className="text-zinc-700 text-[9px] mt-2">
              Toegewezen door {schema.assigned_by_naam} op {fmtTs(schema.assigned_at)}
            </p>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-zinc-500 text-sm">Geen schema toegewezen</p>
            {schemas.length === 0 && (
              <p className="text-zinc-600 text-xs mt-1">Maak eerst een schema aan via de Schemabibliotheek</p>
            )}
          </div>
        )}
      </div>

      {/* ── Opgeslagen maaltijden ─────────────────────────────────────────── */}
      <div className="bg-[#0f0f0f] border border-white/6 rounded-xl overflow-hidden">
        <button onClick={() => setEigenOpen(a => !a)}
          data-testid="eigen-maaltijden-toggle"
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/3 transition-colors">
          <div className="flex items-center gap-2">
            <Bookmark size={13} className="text-zinc-500" />
            <p className="text-zinc-400 text-sm font-semibold">Opgeslagen maaltijden</p>
            {eigenMaaltijden.length > 0 && (
              <span className="bg-white/10 text-zinc-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
                {eigenMaaltijden.length}
              </span>
            )}
          </div>
          {eigenOpen ? <ChevronUp size={14} className="text-zinc-600" /> : <ChevronDown size={14} className="text-zinc-600" />}
        </button>
        {eigenOpen && (
          <div className="border-t border-white/6 px-4 pb-4 pt-3 space-y-2 max-h-64 overflow-y-auto">
            {eigenMaaltijden.length === 0 ? (
              <p className="text-zinc-600 text-sm text-center py-4">
                Sporter heeft nog geen maaltijden opgeslagen
              </p>
            ) : eigenMaaltijden.map(m => (
              <div key={m.id} data-testid={`eigen-maaltijd-coach-${m.id}`}
                className="flex items-center gap-3 py-2 border-b border-white/4 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-semibold truncate">{m.naam}</p>
                  <p className="text-zinc-600 text-[10px] mt-0.5">
                    {m.hoeveelheid}{m.eenheid} ·{" "}
                    <span className="text-[#C9A84C]">{Math.round(m.calorieen)} kcal</span>
                    {" · "}{Math.round(m.eiwitten)}g E · {Math.round(m.koolhydraten)}g K · {Math.round(m.vetten)}g V
                  </p>
                </div>
                <button
                  onClick={() => handleVerwijderEigenMaaltijd(m.id)}
                  disabled={verwijderendId === m.id}
                  data-testid={`verwijder-eigen-coach-${m.id}`}
                  className="p-1.5 text-zinc-600 hover:text-red-400 transition-colors disabled:opacity-40 shrink-0">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Audit log ─────────────────────────────────────────────────────── */}
      <div className="bg-[#0f0f0f] border border-white/6 rounded-xl overflow-hidden">
        <button onClick={() => setAuditOpen(a => !a)}
          data-testid="audit-log-toggle"
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/3 transition-colors">
          <div className="flex items-center gap-2">
            <History size={13} className="text-zinc-500" />
            <p className="text-zinc-400 text-sm font-semibold">Wijzigingshistorie</p>
            {auditLogs.length > 0 && (
              <span className="bg-white/10 text-zinc-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
                {auditLogs.length}
              </span>
            )}
          </div>
          {auditOpen ? <ChevronUp size={14} className="text-zinc-600" /> : <ChevronDown size={14} className="text-zinc-600" />}
        </button>
        {auditOpen && (
          <div className="border-t border-white/6 px-4 pb-4 space-y-2 pt-3 max-h-56 overflow-y-auto">
            {auditLogs.length === 0 ? (
              <p className="text-zinc-600 text-sm text-center py-3">Geen wijzigingen geregistreerd</p>
            ) : auditLogs.map(log => (
              <div key={log.id} data-testid={`audit-log-${log.id}`}
                className="flex items-start gap-3 py-2 border-b border-white/4 last:border-0">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black text-black shrink-0"
                  style={{ background: "#C9A84C" }}>
                  {(log.changed_by_naam || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-xs font-bold">{log.changed_by_naam}</span>
                    <span className="text-[#C9A84C]/70 text-[10px] font-semibold">
                      {ACTIE_LABELS[log.actie] || log.actie}
                    </span>
                  </div>
                  <p className="text-zinc-500 text-[10px] mt-0.5 truncate">{log.details}</p>
                  <p className="text-zinc-700 text-[9px] mt-0.5">{fmtTs(log.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Schema toewijzen modal ─────────────────────────────────────────── */}
      {schemaModal && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/75 p-4"
          onClick={e => e.target === e.currentTarget && setSchemaModal(false)}>
          <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold">Schema toewijzen</h3>
              <button onClick={() => setSchemaModal(false)}><X size={16} className="text-zinc-500" /></button>
            </div>
            {schemas.length === 0 ? (
              <div className="text-center py-4">
                <AlertCircle size={20} className="text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">Geen voedingsschema's beschikbaar.</p>
                <p className="text-zinc-600 text-xs mt-1">Maak eerst schema's aan in de Schemabibliotheek.</p>
              </div>
            ) : (
              <>
                <select value={selectedSch} onChange={e => setSelectedSch(e.target.value)}
                  data-testid="schema-select"
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40 cursor-pointer mb-4">
                  <option value="">-- Kies schema --</option>
                  {schemas.map(s => <option key={s.id} value={s.id}>{s.naam} ({s.calorieen_doel} kcal)</option>)}
                </select>
                <div className="flex gap-2">
                  <button onClick={() => setSchemaModal(false)}
                    className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold hover:text-white transition-colors">
                    Annuleer
                  </button>
                  <button onClick={handleToewijzenSchema} disabled={!selectedSch || saving}
                    data-testid="bevestig-schema-btn"
                    className="flex-[2] py-2.5 rounded-xl text-black text-sm font-bold disabled:opacity-40 transition-all"
                    style={{ background: "#C9A84C" }}>
                    {saving ? "Toewijzen..." : "Toewijzen"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Toast ──────────────────────────────────────────────────────────── */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-[70] flex items-center gap-3 px-5 py-3.5 rounded-xl text-sm font-semibold shadow-2xl ${
          toast.t === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"
        }`}>
          {toast.t === "success"
            ? <Check size={14} className="text-[#C9A84C]" />
            : <AlertCircle size={14} className="text-red-400" />}
          {toast.msg}
        </div>
      )}

      {/* ── Intake Modal ────────────────────────────────────────────────────── */}
      {showIntake && (
        <VoedingIntakeModal
          token={token}
          sporter={sporter}
          initialData={intake}
          initialBerekening={berekening}
          onClose={() => setShowIntake(false)}
          onSaved={(ber) => {
            setBerekening(ber);
            setShowIntake(false);
            showToast("Voedingsprofiel opgeslagen! Macro-doelen bijgewerkt.");
            loadAll();
          }}
        />
      )}
    </div>
  );
}
